import type {
  ExecutionBoqLine,
  ExecutionNormCell,
  ExecutionSlotDef,
  ExecutionSlotKey,
} from "../../../api/ActivityExecution";

// CLIENT-SIDE PORT of the backend's src/utils/execution-norm.util.ts.
//
// It exists so the daily entry form can show calculated actuals as the user
// types, without a round trip per keystroke. It is a PREVIEW: the server
// recomputes on save and its response is authoritative. Same pattern as
// shared/taskPrice.ts mirroring task-price.helper.ts.
//
// If you change the arithmetic here, change it there — and vice versa. The
// rules below are load-bearing in both places:
//
//   1. An ABSENT norm cell contributes nothing and is not recorded. An explicit
//      0 IS a configured value and contributes 0. The UI renders the first as a
//      muted dash and the second as a real 0.
//   2. A slot with zero entered quantity contributes nothing.
//   3. Round the PRODUCT, never the norm — the source workbook carries 12+
//      decimals and truncating the norm drifts B1.1 by ~4 KWh over a 400 m drive.
//   4. A line with no contributions produces no row.
//   5. An all-zero result produces no row either: nothing was physically made.

export const EXECUTION_SLOT_KEYS: ExecutionSlotKey[] = [
  "SLOT_1",
  "SLOT_2",
  "SLOT_3",
  "SLOT_4",
  "SLOT_5",
];

export const DEFAULT_SLOT_LABELS: Record<ExecutionSlotKey, string> = {
  SLOT_1: "Class-I",
  SLOT_2: "Class-II",
  SLOT_3: "Class-III",
  SLOT_4: "Class-IV",
  SLOT_5: "Class-V",
};

export const round4 = (value: number): number =>
  Math.round((Number(value) + Number.EPSILON) * 10000) / 10000;

export interface PreviewContribution {
  slotKey: ExecutionSlotKey;
  slotLabel: string;
  qty: number;
  norm: number;
  value: number;
}

export interface PreviewLine {
  taskId: number;
  taskSequenceId: string;
  boqNo: string;
  taskName: string;
  workUomName: string;
  actualValue: number;
  contributions: PreviewContribution[];
}

const toNumber = (value: unknown): number => {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// 'AE-0101/B3.1.1(1)' -> 'B3.1.1(1)'.
//
// Feed this taskSequenceId, NOT internalTaskNumber. The two are different:
//   taskSequenceId     = '<activityCode>/<boqNo>[(n)]' — unique (963/963 on URI)
//   internalTaskNumber = the bare 'B3.1.1' — NOT unique within an activity;
//                        24 of URI's 963 tasks collide, so showing it as the BOQ
//                        No renders two identical-looking rows for a split line.
// The '(n)' suffix is what disambiguates them and is deliberately kept.
export const boqNoOf = (taskSequenceId: string): string => {
  if (!taskSequenceId) return "";
  const slash = taskSequenceId.indexOf("/");
  return slash === -1 ? taskSequenceId : taskSequenceId.slice(slash + 1);
};

/** Index the sparse norm matrix as taskId -> slotKey -> norm. */
export const buildNormIndex = (
  norms: ExecutionNormCell[]
): Map<number, Map<ExecutionSlotKey, number>> => {
  const index = new Map<number, Map<ExecutionSlotKey, number>>();
  for (const cell of norms || []) {
    const norm = Number(cell.normValue);
    if (!Number.isFinite(norm)) continue;
    let row = index.get(cell.taskId);
    if (!row) {
      row = new Map<ExecutionSlotKey, number>();
      index.set(cell.taskId, row);
    }
    row.set(cell.slotKey, norm);
  }
  return index;
};

/**
 * ActualValue(task) = SUM_slots( actualExecutionQty(slot) x normValue(task, slot) )
 */
export const computeEntryPreview = (params: {
  slotQuantities: Partial<Record<ExecutionSlotKey, number | string>>;
  slotDefs: ExecutionSlotDef[];
  norms: ExecutionNormCell[];
  tasks: ExecutionBoqLine[];
}): PreviewLine[] => {
  const { slotQuantities, slotDefs, norms, tasks } = params;

  const activeSlots = new Map<ExecutionSlotKey, ExecutionSlotDef>();
  for (const def of slotDefs || []) {
    if (def.isActive === false) continue;
    activeSlots.set(def.slotKey, def);
  }

  const normIndex = buildNormIndex(norms);
  const taskById = new Map<number, ExecutionBoqLine>(
    (tasks || []).map((task) => [task.taskId, task])
  );

  const lines: PreviewLine[] = [];
  normIndex.forEach((cells, taskId) => {
    const contributions: PreviewContribution[] = [];
    for (const slotKey of EXECUTION_SLOT_KEYS) {
      const def = activeSlots.get(slotKey);
      if (!def) continue;
      if (!cells.has(slotKey)) continue; // rule 1 — not applicable
      const qty = toNumber(slotQuantities?.[slotKey]);
      if (qty === 0) continue; // rule 2
      const norm = cells.get(slotKey) as number;
      contributions.push({
        slotKey,
        slotLabel: def.slotLabel,
        qty: round4(qty),
        norm,
        value: round4(qty * norm), // rule 3
      });
    }
    if (!contributions.length) return; // rule 4
    if (contributions.every((contribution) => contribution.value === 0)) return; // rule 5

    const task = taskById.get(taskId);
    lines.push({
      taskId,
      taskSequenceId: task?.taskSequenceId || "",
      boqNo: boqNoOf(task?.taskSequenceId || "") || task?.internalTaskNumber || "",
      taskName: task?.taskName || "",
      workUomName: task?.workUomName || "",
      actualValue: round4(
        contributions.reduce((acc, contribution) => acc + contribution.value, 0)
      ),
      contributions,
    });
  });

  return lines.sort((a, b) =>
    (a.boqNo || "").localeCompare(b.boqNo || "", undefined, { numeric: true })
  );
};

/** 'YYYY-MM-DD' for a Date, using LOCAL getters (toISOString would shift IST back a day). */
export const toDateOnly = (value: Date): string => {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, "0");
  const day = `${value.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Quantity formatter for derived values. 4 dp is the storage grain, but trailing
// zeros are noise on screen, so they are trimmed.
export const fmtQty = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === "") return "—";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "—";
  return parsed
    .toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
};

// Norms are displayed at up to 4 dp but STORED and calculated at full precision.
export const fmtNorm = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === "") return "—";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "—";
  return parsed.toLocaleString("en-IN", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
};

// ------------------------- chainage stretches (web port) -------------------
// Mirrors the mobile engine and the server guards — one set of rules.

export interface ExecutionStretch {
  fromChainage: string;
  toChainage: string;
  length?: string;
}

export const isCompleteStretch = (stretch: ExecutionStretch): boolean =>
  stretch.fromChainage !== "" &&
  stretch.toChainage !== "" &&
  Number.isFinite(Number(stretch.fromChainage)) &&
  Number.isFinite(Number(stretch.toChainage));

export const stretchLength = (stretch: ExecutionStretch): number =>
  isCompleteStretch(stretch)
    ? round4(Math.abs(Number(stretch.toChainage) - Number(stretch.fromChainage)))
    : 0;

export const sumStretches = (stretches: ExecutionStretch[]): number =>
  round4((stretches || []).reduce((total, s) => total + stretchLength(s), 0));

export const isLinearUom = (uomName: string | null | undefined): boolean =>
  /^(r\.?\s*m\.?t?|m|mtr|mtrs|met(er|re)s?|running\s*met(er|re)s?)$/i.test(
    (uomName || "").trim()
  );

/** Rock-class slots are ALWAYS metres of advance; the cum caverns label by method. */
export const isRockClassLabel = (slotLabel: string | null | undefined): boolean =>
  /class[\s-]+[ivx]+\b/i.test(slotLabel || "");

export const supportsStretches = (slot: {
  uomName?: string;
  slotLabel?: string;
}): boolean =>
  isLinearUom(slot.uomName) || (!slot.uomName && isRockClassLabel(slot.slotLabel));

export const stretchesOverlap = (
  a: ExecutionStretch,
  b: ExecutionStretch
): boolean => {
  if (!isCompleteStretch(a) || !isCompleteStretch(b)) return false;
  const aLo = Math.min(Number(a.fromChainage), Number(a.toChainage));
  const aHi = Math.max(Number(a.fromChainage), Number(a.toChainage));
  const bLo = Math.min(Number(b.fromChainage), Number(b.toChainage));
  const bHi = Math.max(Number(b.fromChainage), Number(b.toChainage));
  return Math.min(aHi, bHi) - Math.max(aLo, bLo) > 0.001;
};

export interface DayOverlapConflict {
  otherSlotKey: ExecutionSlotKey;
  otherFrom: string;
  otherTo: string;
}

/**
 * Day-level overlap check ACROSS ALL CLASSES — one metre of tunnel is one rock
 * class. Keyed `${slotKey}:${index}`; mirrors the server guard so the form can
 * refuse what the server would refuse.
 */
export const dayOverlapConflicts = (
  stretchesBySlot: Partial<Record<ExecutionSlotKey, ExecutionStretch[]>>
): Map<string, DayOverlapConflict> => {
  const all: Array<{
    slotKey: ExecutionSlotKey;
    index: number;
    stretch: ExecutionStretch;
  }> = [];
  for (const slotKey of EXECUTION_SLOT_KEYS) {
    (stretchesBySlot[slotKey] || []).forEach((stretch, index) => {
      if (isCompleteStretch(stretch)) all.push({ slotKey, index, stretch });
    });
  }
  const conflicts = new Map<string, DayOverlapConflict>();
  for (let i = 0; i < all.length; i += 1) {
    for (let j = i + 1; j < all.length; j += 1) {
      const a = all[i];
      const b = all[j];
      if (!stretchesOverlap(a.stretch, b.stretch)) continue;
      const aKey = `${a.slotKey}:${a.index}`;
      const bKey = `${b.slotKey}:${b.index}`;
      if (!conflicts.has(aKey)) {
        conflicts.set(aKey, {
          otherSlotKey: b.slotKey,
          otherFrom: b.stretch.fromChainage,
          otherTo: b.stretch.toChainage,
        });
      }
      if (!conflicts.has(bKey)) {
        conflicts.set(bKey, {
          otherSlotKey: a.slotKey,
          otherFrom: a.stretch.fromChainage,
          otherTo: a.stretch.toChainage,
        });
      }
    }
  }
  return conflicts;
};

export const incompleteStretchCount = (
  stretchesBySlot: Partial<Record<ExecutionSlotKey, ExecutionStretch[]>>
): number => {
  let count = 0;
  for (const slotKey of EXECUTION_SLOT_KEYS) {
    for (const stretch of stretchesBySlot[slotKey] || []) {
      if (!isCompleteStretch(stretch)) count += 1;
    }
  }
  return count;
};
